import React, { useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { Card, Form } from 'react-bootstrap';
/* eslint-disable import/named */
import { MultiValue } from 'react-select';

import ValidationAlert from '../../../app/components/ValidationAlert';
import FormRow from '../../../app/components/FormRow';
import RegisterMultiSelector from '../Selects/RegisterMultiSelector';
import HealthcareFacilitySelector from '../Selects/HealthcareFacilitySelector';
import { Option } from '../Selects/Selector';
import { ReactSelectOption } from '../Selects/SortableSelect';
import momencik from '../../../common/momencik';
import { CreateUpdateRegisterNumberingDto } from '../../../common/spdCore/autogenerated/spdApiClient/models/CreateUpdateRegisterNumberingDto';

interface RegisterNumberingEditorProps {
  id?: string;
  registerNumbering: CreateUpdateRegisterNumberingDto;
  show: boolean;
  onHide: () => void;
  onSave?: (
    registerNumbering: CreateUpdateRegisterNumberingDto,
    id?: string,
  ) => Promise<void>;
}

export const RegisterNumberingEditor: React.FC<
  RegisterNumberingEditorProps
> = ({
  id,
  registerNumbering: initialRegisterNumbering,
  show,
  onHide,
  onSave,
}) => {
  const [registerNumbering, setRegisterNumbering] = useState(
    initialRegisterNumbering,
  );
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);

  useEffect(() => {
    if (show) {
      setSaveErrors([]);
      setShowSaveErrors(false);
      setRegisterNumbering(initialRegisterNumbering);
    }
  }, [show, initialRegisterNumbering]);

  const handleSave = async () => {
    if (onSave) {
      try {
        await onSave(registerNumbering, id);
        onHide();
      } catch (error) {
        const errorMessages = Array.isArray(error) ? error : [String(error)];
        setSaveErrors(errorMessages);
        setShowSaveErrors(true);
      }
    } else {
      onHide();
    }
  };

  const propertyChange = (obj: Partial<CreateUpdateRegisterNumberingDto>) => {
    setRegisterNumbering({ ...registerNumbering, ...obj });
  };

  const handleRegisterMultiChange = (
    options: MultiValue<ReactSelectOption>,
  ) => {
    const registerIds = (options as ReactSelectOption[]).map(o => o.value);
    propertyChange({ registerIds });
  };

  const handleHealthcareFacilityChange = (option: Option) => {
    propertyChange({ healthcareFacilityId: option.value });
  };

  const handleDateChange = (
    dateValue: string,
    field: 'activeFrom' | 'activeTo',
  ) => {
    propertyChange({
      [field]: dateValue ? new Date(dateValue) : undefined,
    });
  };

  return (
    <Modal
      onHide={onHide}
      show={show}
      centered={true}
      keyboard={true}
      backdrop="static"
      size="xl"
    >
      <Modal.Header closeButton>
        <Modal.Title>Dodawanie nowej numeracji rejestru</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Card className="mb-2">
          <Card.Header>Numeracja Rejestru</Card.Header>
          <Card.Body>
            <Form className="d-grid gap-3">
              <FormRow controlId="registers" label="Rejestry">
                <RegisterMultiSelector
                  selectedRegisterIds={registerNumbering.registerIds || []}
                  onChange={handleRegisterMultiChange}
                />
              </FormRow>

              <FormRow controlId="healthcareFacility" label="Placówka">
                <HealthcareFacilitySelector
                  value={registerNumbering.healthcareFacilityId || undefined}
                  onChange={handleHealthcareFacilityChange}
                  readOnly={true}
                />
              </FormRow>

              <FormRow controlId="symbolDoctor" label="Symbol Doktor">
                <Form.Control
                  type="text"
                  value={registerNumbering.symbolDoctor || ''}
                  onChange={e =>
                    propertyChange({ symbolDoctor: e.target.value })
                  }
                />
              </FormRow>

              <FormRow controlId="symbolPsychological" label="Symbol Psycholog">
                <Form.Control
                  type="text"
                  value={registerNumbering.symbolPsychological || ''}
                  onChange={e =>
                    propertyChange({
                      symbolPsychological: e.target.value,
                    })
                  }
                />
              </FormRow>

              <FormRow controlId="activeFrom" label="Aktywny od">
                <Form.Control
                  type="date"
                  value={momencik(registerNumbering.activeFrom, 'YYYY-MM-DD')}
                  onChange={e => handleDateChange(e.target.value, 'activeFrom')}
                />
              </FormRow>
            </Form>
          </Card.Body>
        </Card>
        <ValidationAlert
          show={showSaveErrors}
          errors={saveErrors}
          className="mt-3"
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Zamknij
        </Button>
        <Button variant="primary" onClick={handleSave}>
          Zapisz
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
